import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.primaryText};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${(props) => props.theme.secondaryText};
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const BodyCard = styled.div`
  background: ${(props) => props.theme.cardBg};
  border-radius: 0.5rem;
  padding: 2rem;
  box-shadow: ${(props) => props.theme.shadow};
  border: 1px solid ${(props) => props.theme.border};
  text-align: left;
`;

const BodyText = styled.div`
  color: ${(props) => props.theme.primaryText};
  font-size: 1rem;
  line-height: 1.7;

  p {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const AboutUsPage = () => {
  const { theme } = useTheme();
  usePageTitle('About Slick Picks');

  return (
    <Container>
      <PageMeta description="Learn the origin story behind Slick Picks, from manual MLB betting research to a fully automated, machine learning–driven player prop prediction platform." />
      <Title theme={theme}>About Us</Title>
      <Subtitle theme={theme}>
        Our origin story.
      </Subtitle>
      <BodyCard theme={theme}>
        <BodyText theme={theme}>
          <p>Slick Picks started out of a desire to automate a manual process.</p>

          <p>A few years ago, I had a very successful season betting on MLB. I used my baseball experience and knowledge to identify favorable matchups. I would look for games where there was a vulnerable pitcher slated to start. I would look at the opposing roster and identify players with a good probability to succeed against the mediocre starter. It worked.</p>

          <p>But it took a long time.</p>

          <p>Every morning I was spending 30 minutes crunching the numbers so I could fabricate more interest in that day&apos;s slate of games.</p>

          <p>I started building a data pipeline from scratch. The side project was an excuse for me to get better at a skill I had used passively during my 9-5 over the years. Win, win.</p>

          <p>But it took a long time.</p>

          <p>After about one entire season of very part-time effort dedicated to this endeavor, I had finally managed to build a pipeline I was proud of. I was getting the data I needed and wrote a custom algorithm of conditional filters to return a short list of players meeting my criteria. It worked.</p>

          <p>Kinda.</p>

          <p>It was janky and still manual. I was running code locally and couldn&apos;t access the results unless I was at home.</p>

          <p>Not ideal.</p>

          <p>So I got back to work. I started envisioning a product with me as the primary user. What would make my life easier? How could I easily get picks suggested to me in a straightforward way, from anywhere? So I did the sensical thing.</p>

          <p>I decided to build a web app.</p>

          <p>The timing was right. LLMs were making headlines and I had just listened to the Lex Fridman podcast hosting the founders of Cursor. I was hooked, and I started expanding my backend and materializing a frontend. I had built an app. But LLMs weren&apos;t the only thing I had recently learned.</p>

          <p>I learned machine learning.</p>

          <p>Inspired by industry peers of my 9-5, I enrolled in a course by MIT to learn how to build, test, and deploy ML models. I applied that knowledge to the data I was ingesting from my OG pipeline. At this point I had years of data which, by design, I was able to accurately train to mitigate overfitting. My suggested picks were not backed by data and not my finger-in-the-wind deterministic criteria.</p>

          <p>But I wasn&apos;t winning much.</p>

          <p>I lost sight of the original intent. I had focused more on the app&apos;s code and applying ML than on the outcome of winning bets. I had lost track of my successful betting strategy. I was wrapped up in powerful things, but not applying them effectively. At season&apos;s end, I took a step back and got back to my roots. I needed to reset my aim on the outcome. Winning bets.</p>

          <p>Here we are.</p>

          <p>In preparation for this season, I have been developing features to focus specifically on profitability. I have incorporated odds for each pick. I am calculating edge. I have trained new models targeting new outcomes to identify more advantageous bets. I have introduced insights to identify favorable matchups (one component of my initial betting strategy).</p>

          <p>But that&apos;s not all.</p>

          <p>I&apos;m distilling all of this into Picks of the Day and Parlays of the Day. I&apos;m providing actionable insights without the research or guesswork. We won&apos;t always win, but we&apos;ll be betting with data-backed confidence instead of vibes.</p>

          <p>I&apos;m ready to win.</p>
        </BodyText>
      </BodyCard>
    </Container>
  );
};

export default AboutUsPage;
